import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { serverHealthApi } from '../services/api'
import { useAuth } from './AuthContext'

const ServerHealthContext = createContext({
  alerts: [],
  visibleAlerts: [],
  dismissAlert: () => {}
})

export function ServerHealthProvider({ children }) {
  const { user } = useAuth()
  const [alerts, setAlerts] = useState([])
  const [dismissed, setDismissed] = useState({}) // { type: true }

  const fetchAlerts = useCallback(async () => {
    if (!user) {
      setAlerts([])
      return
    }

    try {
      const response = await serverHealthApi.getAlerts()
      if (response.data?.success) {
        const newAlerts = response.data.alerts || []
        setAlerts(newAlerts)

        // Un-dismiss alerts that have cleared and come back
        setDismissed(prev => {
          const activeTypes = new Set(newAlerts.map(a => a.type))
          const updated = { ...prev }
          let changed = false
          for (const type of Object.keys(updated)) {
            if (!activeTypes.has(type)) {
              delete updated[type]
              changed = true
            }
          }
          return changed ? updated : prev
        })
      }
    } catch (error) {
      // Silent fail - don't spam console for a background poll
    }
  }, [user])

  // Poll every 30 seconds
  useEffect(() => {
    if (!user) return

    fetchAlerts()
    const interval = setInterval(fetchAlerts, 30000)
    return () => clearInterval(interval)
  }, [user, fetchAlerts])

  const dismissAlert = useCallback((type) => {
    setDismissed(prev => ({ ...prev, [type]: true }))
  }, [])

  const visibleAlerts = alerts.filter(a => !dismissed[a.type])

  return (
    <ServerHealthContext.Provider value={{ alerts, visibleAlerts, dismissAlert }}>
      {children}
    </ServerHealthContext.Provider>
  )
}

export function useServerHealth() {
  return useContext(ServerHealthContext)
}
